<template>
  <v-container fluid class="pa-1">
    <v-card flat>
      <v-toolbar flat>
        <v-tooltip top v-if="!foldersDrawer">
          <template #activator="{ on }">
            <v-btn icon v-on="on" @click="foldersDrawer = true">
              <v-icon>mdi-folder-multiple</v-icon>
            </v-btn>
          </template>
          <span>Open folders</span>
        </v-tooltip>
        <v-breadcrumbs large :items="pathItems">
          <template #divider>
            <v-icon>mdi-chevron-right</v-icon>
          </template>
          <template #item="{ item }">
            <v-breadcrumbs-item>
              <FeathersVuexGet
                v-if="item !== 'root'"
                v-slot="{ item: folder }"
                service="directories"
                :id="item"
              >
                <router-link
                  :to="{ name: 'Folders', params: { folderId: item } }"
                  class="folder-name"
                >
                  {{ folder ? folder.name : item }}
                </router-link>
              </FeathersVuexGet>
              <span v-else>
                <router-link :to="{ name: 'Folders' }" class="folder-name"> Home </router-link>
              </span>
            </v-breadcrumbs-item>
          </template>
        </v-breadcrumbs>
        <v-spacer />
        <v-scale-transition origin="center right">
          <v-card v-show="selectedItem !== null && selectedItem._type === 'case'" flat>
            <DialogForm
              :title="`Move to ${moveDestination.name || 'folder...'}`"
              icon="mdi-folder-move-outline"
              tooltip="Move to..."
              :submit="moveItems()"
              :disabled="selectedItem && selectedItem.locked"
              :dialog.sync="moveCaseDialog"
              :max-width="0"
              :dialog-props="{ width: 'unset' }"
              @click.stop
              @open="setMoveToValue"
            >
              <template #default>
                <FolderTreeSelect v-model="moveDestination"></FolderTreeSelect>
              </template>
            </DialogForm>
            <DialogForm
              v-if="selectedItem && !selectedMultiple"
              title="Edit Case"
              icon="mdi-pencil-outline"
              :submit="updateCase(selectedItem)"
              :disabled="selectedItem && selectedItem.locked"
              tooltip="Rename case"
              :dialog.sync="editCaseDialog"
              @click.stop
            >
              <template #default>
                <v-text-field
                  label="Name"
                  v-model="selectedItem.name"
                  counter="64"
                  :rules="[$utils.validation.required, $utils.validation.counter(64)]"
                  maxlength="64"
                ></v-text-field>
                <v-textarea filled label="Notes" v-model="selectedItem.notes"></v-textarea>
              </template>
            </DialogForm>
            <v-tooltip top>
              <template #activator="{ on: onTooltip }">
                <v-btn
                  icon
                  @click.stop="duplicateItems"
                  :disabled="selectedItem && selectedItem.locked"
                  v-on="onTooltip"
                >
                  <v-icon>mdi-file-document-multiple-outline</v-icon>
                </v-btn>
              </template>
              <span>{{ selectedMultiple ? "Duplicate" : "Duplicate case" }}</span>
            </v-tooltip>
            <DialogForm
              v-if="selectedItem && !selectedMultiple"
              title="Copy Case"
              icon="mdi-content-copy"
              :disabled="selectedItem && selectedItem.locked"
              tooltip="Remodel case"
              maxWidth="600"
              :submit="remodelCase(selectedItem.id)"
              :dialog.sync="remodelCaseDialog"
              @open="fillCopyForm(selectedItem)"
              @click.stop
            >
              <template #default>
                <v-text-field
                  label="Case Name"
                  v-model="copyForm.name"
                  counter="64"
                  :rules="[$utils.validation.required, $utils.validation.counter(64)]"
                  maxlength="64"
                ></v-text-field>
                <v-switch
                  v-model="copyForm.disableStorage"
                  label="Disable Storage Expansion"
                  dense
                />
                <v-switch
                  v-model="copyForm.createDelta"
                  label="Create Delta against original case"
                  dense
                />
                <v-text-field
                  v-if="copyForm.createDelta"
                  v-model="copyForm.deltaName"
                  :rules="[$utils.validation.required]"
                  label="Delta Name"
                  class="pl-6 pr-4"
                />
                <v-switch
                  v-model="copyForm.keepResults"
                  label="Keep previous results"
                  hint="Keep Alternative Analysis and Production Cost results when updating case"
                  persistent-hint
                  dense
                />
              </template>
            </DialogForm>
            <v-tooltip top>
              <template #activator="{ on: onTooltip }">
                <v-btn icon v-on="onTooltip" @click.stop="openInNewMultiple"
                  ><v-icon>mdi-open-in-new</v-icon>
                </v-btn>
              </template>
              <span>Open in new tab</span>
            </v-tooltip>
            <v-tooltip top>
              <template #activator="{ on: onTooltip }">
                <v-btn
                  icon
                  @click.stop="deleteItems"
                  :disabled="selectedItem && selectedItem.locked"
                  v-on="onTooltip"
                  ><v-icon>mdi-trash-can-outline</v-icon>
                </v-btn>
              </template>
              <span>Delete</span>
            </v-tooltip>
          </v-card>
        </v-scale-transition>
        <div>
          <v-text-field
            v-model="searchText"
            filled
            rounded
            placeholder="Search"
            prepend-inner-icon="mdi-magnify"
            dense
            class="pt-6 mx-2"
          ></v-text-field>
        </div>
        <DialogForm
          title="Create new case"
          max-width="600px"
          :submit="addCase(newCaseName)"
          :dialog.sync="newCaseDialog"
        >
          <template #activator="{ on: onDialog }">
            <v-tooltip top>
              <template #activator="{ on: onTooltip }">
                <v-btn class="my-0" icon large v-on="onDialog">
                  <v-icon v-on="onTooltip">mdi-file-plus-outline</v-icon>
                </v-btn>
              </template>
              <span>Create case</span>
            </v-tooltip>
          </template>
          <template #default>
            <v-text-field
              v-model="newCaseName"
              label="Name"
              placeholder="New Case"
              counter="64"
              :rules="[
                $utils.validation.required,
                $utils.validation.counter(64),
                $utils.validation.specialCharacters,
              ]"
              maxlength="64"
            ></v-text-field>
          </template>
        </DialogForm>
        <DialogForm
          title="Create new folder"
          max-width="600px"
          :submit="createFolder(newFolderName)"
        >
          <template #activator="{ on: onDialog }">
            <v-tooltip top>
              <template #activator="{ on: onTooltip }">
                <v-btn class="my-0" icon large v-on="onDialog">
                  <v-icon v-on="onTooltip">mdi-folder-plus-outline</v-icon>
                </v-btn>
              </template>
              <span>Create folder</span>
            </v-tooltip>
          </template>
          <template #default>
            <v-text-field
              v-model="newFolderName"
              label="Name"
              placeholder="New Folder"
              counter="64"
              :rules="[
                $utils.validation.required,
                $utils.validation.counter(64),
                $utils.validation.specialCharacters,
              ]"
              maxlength="64"
            ></v-text-field>
          </template>
        </DialogForm>
        <v-card height="60" flat class="ml-2" max-width="160">
          <v-select
            v-model="sortBy"
            label="Sort by"
            :items="sortItems"
            dense
            class="pt-4"
            hide-details
          />
        </v-card>
        <DialogForm
          v-if="selectedItem && selectedItem._type === 'folder'"
          title="Rename Folder"
          :dialog.sync="editFolderDialog"
          :submit="updateFolder(selectedItem)"
          no-activator
          @click.stop
        >
          <template #default>
            <v-text-field
              label="Name"
              v-model="selectedItem.name"
              counter="64"
              :rules="[$utils.validation.required, $utils.validation.counter(64)]"
              maxlength="64"
            ></v-text-field>
          </template>
        </DialogForm>
        <DialogForm
          v-if="selectedItem && selectedItem._type === 'folder'"
          :title="`Move to ${moveDestination.name || 'folder...'}`"
          :dialog.sync="moveFolderDialog"
          :submit="moveFolder(selectedItem)"
          no-activator
          :max-width="0"
          :dialog-props="{ width: 'unset' }"
          @open="setMoveToValue"
          @click.stop
        >
          <template #default>
            <FolderTreeSelect v-model="moveDestination"></FolderTreeSelect>
          </template>
        </DialogForm>
      </v-toolbar>
      <v-divider />
      <v-card-text>
        <FolderTree
          :items="computedTableItems"
          :calculations="calculations"
          :search.sync="searchText"
          :selected.sync="selectedItems"
          :sort-by="sortBy"
          @case-menu-click="caseMenuClick"
          @folder-menu-click="folderMenuClick"
          ref="FolderTree"
        ></FolderTree>
      </v-card-text>
      <v-snackbar v-model="snackbar.state" :timeout="2000">
        {{ snackbar.text }}
        <template #action="{ attrs }">
          <v-btn color="primary" text v-bind="attrs" @click="snackbar.state = false"> Close </v-btn>
        </template>
      </v-snackbar>
    </v-card>
  </v-container>
</template>

<script>
import { mapActions } from "vuex";
import { makeGetMixin } from "feathers-vuex";
import calculationsMixin from "@/mixins/calculationsMixin";

export default {
  name: "Folders",
  components: {
    DialogForm: () => import("@/components/DialogForm.vue"),
    FolderTree: () => import("@/components/FolderTree.vue"),
    FolderTreeSelect: () => import("@/components/FolderTreeSelect.vue"),
  },
  mixins: [
    calculationsMixin,
    makeGetMixin({
      service: "directories",
      id: "currentFolderId",
      item: "currentFolder",
      watch: "directoryId",
    }),
  ],
  props: {},
  data() {
    return {
      foldersDrawer: false,
      searchText: "",
      selectedItems: [],
      moveDestination: {},
      sortBy: "updated",
      sortItems: [
        { text: "Last Modified", value: "updated" },
        { text: "Name", value: "name" },
      ],
      rootChildren: null,
      parentFolder: null,
      newFolderName: "",
      editFolderDialog: false,
      moveCaseDialog: false,
      moveFolderForm: "",
      moveFolderDialog: false,
      editCaseDialog: false,
      remodelCaseDialog: false,
      newCaseName: "",
      newCaseDialog: false,
      newCaseFolder: null,
      copyForm: {
        id: "",
        name: "",
        disableStorage: false,
        createDelta: false,
        deltaName: "",
        keepResults: false,
      },
      snackbar: {
        text: "Link copied",
        state: false,
      },
    };
  },
  computed: {
    currentFolderId() {
      return this.$route.params.folderId;
    },
    computedTableItems() {
      if (this.currentFolder) {
        return [
          ...this.currentFolder.directories.map((v) => ({
            ...v,
            _type: "folder",
            children: [],
          })),
          ...this.currentFolder.cases.map((v) => ({ ...v, _type: "case" })),
        ];
      }
      if (this.rootChildren) {
        return [
          ...this.rootChildren.directories.map((v) => ({
            ...v,
            _type: "folder",
            children: [],
          })),
          ...this.rootChildren.cases.map((v) => ({ ...v, _type: "case" })),
        ];
      }
      return [];
    },
    pathItems() {
      const pathItems = this.currentFolder ? this.currentFolder.path.split(".") : [];
      if (pathItems.length) {
        return ["root", ...pathItems];
      }
      return ["root"];
    },
    directoryId() {
      return this.$route.params.folderId;
    },
    selectedItem() {
      return this.selectedItems.length ? this.selectedItems[0] : null;
    },
    selectedMultiple() {
      return Boolean(this.selectedItems.length > 1);
    },
    folderTreeValue() {
      return this.currentFolder
        ? { ...this.currentFolder, _type: "folder" }
        : { id: null, name: "Home", _type: "folder" };
    },
  },
  watch: {
    directoryId(v) {
      this.getParentFolder();
      if (!v) {
        this.getRoot();
      }
    },
  },
  beforeCreate() {},
  created() {},
  beforeMount() {},
  mounted() {
    if (!this.$route.params.folderId) {
      this.getRoot();
    }
    this.getParentFolder();
  },
  beforeUpdate() {},
  updated() {},
  activated() {},
  deactivated() {},
  beforeDestroy() {},
  destroyed() {},
  methods: {
    ...mapActions({
      getDirectoryAction: "directories/get",
      findDirectories: "directories/find",
      createDirectory: "directories/create",
      patchDirectory: "directories/patch",
      removeDirectory: "directories/confirmRemove",
      createCase: "cases/create",
      loadCase: "cases/get",
      patchCase: "cases/patch",
      removeCase: "cases/confirmRemove",
      downloadCase: "cases/downloadOutputs",
    }),
    caseMenuClick(action, item) {
      switch (action) {
        case "move":
          this.moveCaseDialog = true;
          break;
        case "open":
          this.navigateTo(item);
          break;
        case "openInNew":
          this.openInNew(item);
          break;
        case "getLink":
          this.getLink(item);
          break;
        case "edit":
          this.editCaseDialog = true;
          break;
        case "duplicate":
          this.copyCase(item.id)();
          break;
        case "remodel":
          this.remodelCaseDialog = true;
          break;
        case "downloadOutputs":
          this.downloadCase(item.id);
          break;
        case "delete":
          this.deleteCase(item.id)();
          break;
        default:
          break;
      }
    },
    folderMenuClick(action, item) {
      switch (action) {
        case "openInNew":
          this.openInNew(item);
          break;
        case "getLink":
          this.getLink(item);
          break;
        case "edit":
          this.editFolderDialog = true;
          break;
        case "duplicate":
          this.duplicateFolder(item.id);
          break;
        case "move":
          this.moveFolderDialog = true;
          break;
        case "createCase":
          this.newCaseFolder = item.id;
          this.newCaseDialog = true;
          break;
        case "delete":
          this.deleteFolder(item.id)();
          break;
        default:
          break;
      }
    },
    getRoot() {
      this.findDirectories({
        query: {},
      }).then((r) => {
        [this.rootChildren] = r;
        this.$refs.FolderTree.refreshTree();
      });
    },
    getParentFolder() {
      const parentId = this.currentFolder
        ? this.currentFolder.path.split(".")[this.currentFolder.path.split(".").length - 2]
        : null;
      if (parentId) {
        this.getDirectoryAction(parentId).then((r) => {
          this.parentFolder = r;
        });
      }
    },
    toFolder($e) {
      const item = { _type: "folder", id: $e.id === "root" ? null : $e.id };
      this.navigateTo(item);
    },
    navigateTo(item) {
      // eslint-disable-next-line no-underscore-dangle
      if (item._type === "folder") {
        this.$router.push({ name: "Folders", params: { folderId: item.id } }).catch(() => {});
      } else {
        this.$router.push({ path: `/case/${item.id}/input#Settings` }).catch(() => {});
      }
    },
    openInNew(item) {
      let routeInfo;
      // eslint-disable-next-line no-underscore-dangle
      if (item._type === "folder") {
        routeInfo = this.$router.resolve({ name: "Folders", params: { folderId: item.id } });
      } else {
        routeInfo = this.$router.resolve({ path: `/case/${item.id}/input#Settings` });
      }
      window.open(routeInfo.href, "_blank");
    },
    openInNewMultiple() {
      this.selectedItems.forEach((item) => {
        this.openInNew(item);
      });
    },
    getLink(item) {
      let routeInfo;
      // eslint-disable-next-line no-underscore-dangle
      if (item._type === "folder") {
        routeInfo = this.$router.resolve({ name: "Folders", params: { folderId: item.id } });
      } else {
        routeInfo = this.$router.resolve({ path: `/case/${item.id}/input#Settings` });
      }
      const url = `${window.location.protocol}//${window.location.host}${routeInfo.href}`;
      navigator.clipboard.writeText(url);
      this.snackbar.state = true;
    },
    createFolder(folderName) {
      return () => {
        console.debug("Create Folder", folderName);
        return this.createDirectory({
          name: folderName,
          ...(this.currentFolder ? { path: this.currentFolder.path } : {}),
        }).then((res) => {
          console.debug(res);
          this.newFolderName = "";
          if (!this.directoryId) {
            this.getRoot();
          } else {
            this.getDirectory();
          }
        });
      };
    },
    addCase(name) {
      return () => {
        console.debug("Create Case", name);
        return this.createCase({
          name,
          directory_id: this.newCaseFolder || this.directoryId,
        }).then((res) => {
          console.debug(res);
          this.newCaseName = "";
          this.newCaseFolder = null;
          if (!this.directoryId) {
            this.getRoot();
          } else {
            this.getDirectory();
          }
        });
      };
    },
    updateFolder(_folder) {
      return () => {
        console.debug("Update Folder", _folder);
        const patchedFolder = { name: _folder.name };
        return this.patchDirectory([_folder.id, patchedFolder]).then(() => {
          if (!this.directoryId) {
            this.getRoot();
          } else {
            this.getDirectory();
          }
        });
      };
    },
    updateCase(_case) {
      return () => {
        console.debug("Update Case", _case);
        const patchedCase = { name: _case.name, notes: _case.notes };
        return this.patchCase([_case.id, patchedCase]).then(() => {
          if (!this.directoryId) {
            this.getRoot();
          } else {
            this.getDirectory();
          }
        });
      };
    },
    duplicateItems() {
      const promises = [];
      this.selectedItems.forEach((element) => {
        // eslint-disable-next-line no-underscore-dangle
        if (element._type === "case") {
          promises.push(this.copyCase(element.id)());
          // eslint-disable-next-line no-underscore-dangle
        } else if (element._type === "folder") {
          promises.push(this.duplicateFolder(element.id));
        }
      });
      return Promise.all(promises).then(() => {
        this.selectedItems = [];
      });
    },
    copyCase(caseId, { nameSuffix = "Copy" } = {}) {
      return () =>
        this.loadCase(caseId)
          .then((_case) => {
            const newCase = _case.clone();
            delete newCase.id;
            delete newCase.created;
            delete newCase.updated;
            delete newCase.jobs;
            newCase.name += ` ${nameSuffix}`;
            console.debug("save", _case);
            return newCase.save();
          })
          .then(() => {
            if (!this.directoryId) {
              this.getRoot();
            } else {
              this.getDirectory();
            }
          });
    },
    duplicateFolder(folderId) {
      this.createDirectory([{}, { query: { copy: folderId } }]).then(() => {
        if (!this.directoryId) {
          this.getRoot();
        } else {
          this.getDirectory();
        }
        this.selectedItems = [];
      });
    },
    remodelCase(caseId) {
      return () =>
        this.loadCase(caseId)
          .then((_case) => {
            console.debug(_case);
            const newCase = _case.clone();
            delete newCase.id;
            delete newCase.created;
            delete newCase.updated;
            delete newCase.jobs;

            if (newCase.name === this.copyForm.name) {
              newCase.name += " Copy";
            } else {
              newCase.name = this.copyForm.name;
            }
            if (this.copyForm.disableStorage) {
              newCase.input.storage = newCase.input.storage.map((v) => ({
                ...v,
                expansion_candidate: false,
                maximum_build: Array(v.maximum_build.length).fill(0),
              }));
              newCase.metadata = { bess_expansion: false };
              newCase.name += " No Storage";
            }
            if (this.copyForm.keepResults) {
              newCase.settings.ALTERNATIVE_ANALYSIS.saveOutputs = true;
              newCase.settings.PRODUCTION_COST.saveOutputs = true;
            }
            if (this.copyForm.createDelta) {
              newCase.metadata = { delta_created: true, ...newCase.metadata };
            }
            return newCase.save();
          })
          .then((_case) => {
            if (this.copyForm.createDelta) {
              this.createDelta({
                caseA_scenario: _case.scenario_id,
                caseA_id: this.copyForm.id,
                caseB_scenario: _case.scenario_id,
                caseB_id: _case.id,
                name: this.copyForm.deltaName,
                type: 3,
              });
            }
            if (!this.directoryId) {
              this.getRoot();
            } else {
              this.getDirectory();
            }
          });
    },
    fillCopyForm(item) {
      this.copyForm = {
        id: item.id,
        name: `${item.name} Copy`,
        disableStorage: false,
        createDelta: false,
        deltaName: "",
        keepResults: false,
      };
    },
    setMoveToValue() {
      this.moveDestination = this.currentFolder;
    },
    moveItems() {
      return () => {
        const promises = [];
        this.selectedItems.forEach((element) => {
          // eslint-disable-next-line no-underscore-dangle
          if (element._type === "case") {
            promises.push(this.moveCase(element)());
            // eslint-disable-next-line no-underscore-dangle
          } else if (element._type === "folder") {
            promises.push(this.moveFolder(element)());
          }
        });
        return Promise.all(promises).then(() => {
          this.selectedItems = [];
        });
      };
    },
    moveCase(_case) {
      return () => {
        console.debug("Moved Case", _case);
        const destinationId =
          !this.moveDestination.id || this.moveDestination.id === "root"
            ? null
            : this.moveDestination.id;
        const patchedCase = { directory_id: destinationId };
        return this.patchCase([_case.id, patchedCase]).then(() => {
          if (!this.directoryId) {
            this.getRoot();
          } else {
            this.getDirectory();
          }
          this.moveDestination = {};
        });
      };
    },
    moveFolder(_folder) {
      return () => {
        const path =
          !this.moveDestination.id || this.moveDestination.id === "root"
            ? null
            : this.moveDestination.path;
        console.debug("Moved Folder", _folder);
        const patchedFolder = { path };
        return this.patchDirectory([_folder.id, patchedFolder]).then(() => {
          if (!this.directoryId) {
            this.getRoot();
          } else {
            this.getDirectory();
          }
          this.moveDestination = {};
        });
      };
    },
    deleteItems() {
      const promises = [];
      this.selectedItems.forEach((element) => {
        // eslint-disable-next-line no-underscore-dangle
        if (element._type === "case") {
          promises.push(this.deleteCase(element.id)());
          // eslint-disable-next-line no-underscore-dangle
        } else if (element._type === "folder") {
          promises.push(this.deleteFolder(element.id)());
        }
      });
      return Promise.all(promises).then(() => {
        this.selectedItems = [];
      });
    },
    deleteCase(caseId) {
      return () =>
        this.removeCase(caseId).then(() => {
          if (!this.directoryId) {
            this.getRoot();
          } else {
            this.getDirectory();
          }
          this.selectedItems = [];
        });
    },
    deleteFolder(folderId) {
      return () =>
        this.removeDirectory(folderId).then(() => {
          if (!this.directoryId) {
            this.getRoot();
          } else {
            this.getDirectory();
          }
        });
    },
  },
};
</script>

<style scoped>
.folder-name {
  color: rgba(0, 0, 0, 0.87);
}
.v-treeview-node__label {
  overflow: initial;
  text-overflow: initial;
}
.folders-column {
  border-right: solid 1px rgba(0, 0, 0, 0.12);
}
</style>
